// Thin client for the public marketing-chat backend (`POST /api/ask`, SSE).
//
// The backend already supports multi-turn: send the prior turns as `history`
// and the new question as `message`. It streams Server-Sent Events over a POST
// body (not EventSource, which is GET-only), so we read the ReadableStream and
// parse `data: {json}` frames ourselves.
//
// Endpoint is configured via VITE_CHAT_ENDPOINT. In dev it points at a Vite
// proxy path (see vite.config.ts) so local requests avoid CORS; in prod it is
// https://chat.thebigwhale.io.

export interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatSource {
  title: string
  url: string
}

export interface AskCallbacks {
  onChunk: (text: string) => void
  onSources?: (sources: ChatSource[]) => void
  onDone: () => void
  onError: (message: string) => void
  signal?: AbortSignal
}

const CHAT_ENDPOINT = String(
  import.meta.env.VITE_CHAT_ENDPOINT || 'https://chat.thebigwhale.io',
).replace(/\/+$/, '')

// Same key AuthService writes the session JWT under (its TBWJF field). Read
// straight from storage rather than through the service: the chat widget is
// constructed on its own, with no services injected, and window.tbwServices may
// not be wired yet on first paint.
const TOKEN_KEY = 'tbw-auth-infos'

export class AiChatService {
  // Kept across turns so the backend can correlate a conversation in its logs.
  private sessionId: string | null = null

  async ask(message: string, history: ChatTurn[], cb: AskCallbacks): Promise<void> {
    let res: Response
    // `client: 'webapp'` only ASKS for the member assistant; the server decides.
    // It used to be taken at face value, which meant anyone could post
    // {"client":"webapp"} and reach the member tools. Sending the session JWT is
    // what actually proves the claim — an anonymous caller has no token to send,
    // and a stale one is refused, so the answer degrades to the public
    // assistant rather than failing.
    let token: string | null = null
    try {
      token = localStorage.getItem(TOKEN_KEY)
    } catch {
      // Storage can throw (private mode, blocked cookies). Public mode, then.
      token = null
    }
    try {
      res = await fetch(`${CHAT_ENDPOINT}/api/ask`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          message,
          history,
          client: 'webapp',
          ...(this.sessionId ? { sessionId: this.sessionId } : {}),
        }),
        signal: cb.signal,
      })
    } catch (e) {
      cb.onError((e as Error).message)
      return
    }

    if (!res.ok || !res.body) {
      cb.onError(`chat_http_${res.status}`)
      return
    }

    const reader = res.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      for (;;) {
        const { value, done } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })

        // SSE frames are separated by a blank line.
        let sep: number
        while ((sep = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, sep).trim()
          buffer = buffer.slice(sep + 2)
          if (!frame.startsWith('data:')) continue
          let evt: {
            type?: string
            text?: string
            message?: string
            sessionId?: string
            sources?: ChatSource[]
          }
          try {
            evt = JSON.parse(frame.slice(5).trim())
          } catch {
            continue
          }
          switch (evt.type) {
            case 'session':
              if (evt.sessionId) this.sessionId = evt.sessionId
              break
            case 'sources':
              cb.onSources?.(evt.sources ?? [])
              break
            case 'chunk':
              cb.onChunk(evt.text ?? '')
              break
            case 'done':
              cb.onDone()
              return
            case 'error':
              cb.onError(evt.message ?? 'error')
              return
          }
        }
      }
      // Stream closed without an explicit `done` event — treat as complete.
      cb.onDone()
    } catch (e) {
      if ((e as Error).name === 'AbortError') return
      cb.onError((e as Error).message)
    }
  }

  reset(): void {
    this.sessionId = null
  }
}
